'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/common/Header';
import KeyIndicesPanel from './KeyIndicesPanel';
import SectorPerformanceMatrix from './SectorPerformanceMatrix';
import TopMoversTable from './TopMoversTable';
import WatchlistSidebar from './WatchlistSidebar';
import EconomicCalendar from './EconomicCalendar';
import AISentimentPanel from './AISentimentPanel';
import MarketToolbar from './MarketToolbar';
import ProviderStatusPanel from './ProviderStatusPanel';
import AlertCreationPanel from '@/components/common/AlertCreationPanel';
import Icon from '@/components/ui/AppIcon';

export default function MarketOverviewInteractive() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [lastUpdated, setLastUpdated] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [ingestStatus, setIngestStatus] = useState<string | null>(null);
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [showProviderPanel, setShowProviderPanel] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      router.replace('/login');
    }
  }, [user, authLoading, router]);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const tick = () => {
      setLastUpdated(
        new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })
      );
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);

  // Fetch alert count
  const fetchAlertCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/alerts');
      const json = await res.json();
      setAlertCount((json.alerts || []).filter((a: any) => a.status === 'active').length);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (user) fetchAlertCount();
  }, [user, fetchAlertCount]);

  // Trigger data ingestion from configured providers on mount and every 60s
  const triggerIngestion = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/market/ingest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols: ['SPX', 'NDX', 'DJI', 'RUT', 'VIX', 'DXY', 'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA', 'META', 'SPY', 'QQQ'] }),
      });
      const json = await res.json();
      if (json.count > 0) {
        setIngestStatus(`${json.count} symbols updated via ${json.providersUsed?.join(', ') || 'provider'}`);
        setTimeout(() => setIngestStatus(null), 5000);
      }
    } catch {
      // Silent fail — panels show null state
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    triggerIngestion();
    const id = setInterval(triggerIngestion, 60000);
    return () => clearInterval(id);
  }, [user, triggerIngestion]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setShowAlertPanel(false);
        setMobileSidebarOpen(false);
      }
    },
    []
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      <Header />

      {ingestStatus && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 rounded-lg text-xs text-emerald-400 font-mono shadow-lg">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {ingestStatus}
        </div>
      )}

      <div className="pt-[56px] flex flex-1 overflow-hidden">
        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/60 md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar — desktop: inline, mobile: slide-out drawer */}
        <div className={`
          fixed md:relative z-40 md:z-auto top-[56px] md:top-auto bottom-0 left-0
          transition-transform duration-300 ease-in-out
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}>
          <WatchlistSidebar
            collapsed={sidebarCollapsed && !isMobile}
            onToggle={() => {
              if (isMobile) setMobileSidebarOpen(false);
              else setSidebarCollapsed(v => !v);
            }}
          />
        </div>

        {/* Main Content */}
        <main
          className={`flex-1 flex flex-col overflow-auto transition-all duration-300 ${
            sidebarCollapsed ? 'md:ml-12' : 'md:ml-64'
          } ml-0`}
        >
          {/* Mobile top bar */}
          <div className="flex md:hidden items-center gap-2 px-3 py-2 border-b border-[#1E293B] bg-[#0F172A]">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="p-2 rounded-lg bg-[#1E293B] border border-[#334155] text-slate-400 hover:text-slate-200"
            >
              <Icon name="Bars3Icon" size={16} variant="outline" />
            </button>
            <span className="text-xs text-slate-500 font-mono flex-1">Market Overview</span>
            <button
              onClick={() => setShowProviderPanel(v => !v)}
              className={`p-2 rounded-lg border text-xs font-mono transition-all ${showProviderPanel ? 'bg-sky-500/20 border-sky-500/40 text-sky-400' : 'bg-[#1E293B] border-[#334155] text-slate-400'}`}
            >
              <Icon name="SignalIcon" size={14} variant="outline" />
            </button>
          </div>

          <MarketToolbar
            searchOpen={searchOpen}
            onSearchToggle={setSearchOpen}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
            lastUpdated={lastUpdated}
            onCreateAlert={() => setShowAlertPanel(true)}
            alertCount={alertCount}
          />

          <div className="flex-1 p-3 sm:p-4 space-y-4">
            {/* Provider Status Panel — shown on mobile when toggled, always visible on desktop as collapsible */}
            <div className={`${showProviderPanel ? 'block' : 'hidden'} md:hidden`}>
              <ProviderStatusPanel />
            </div>

            {/* Row 1: Key Indices */}
            <KeyIndicesPanel />

            {/* Row 2: Sector Matrix + AI Sentiment + Provider Status (desktop) */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="xl:col-span-2">
                <SectorPerformanceMatrix />
              </div>
              <div className="xl:col-span-1 space-y-4">
                <AISentimentPanel />
                {/* Provider Status Panel — desktop sidebar */}
                <div className="hidden xl:block">
                  <ProviderStatusPanel />
                </div>
              </div>
            </div>

            {/* Provider Status Panel — tablet (md-xl) */}
            <div className="hidden md:block xl:hidden">
              <ProviderStatusPanel />
            </div>

            {/* Row 3: Top Movers + Economic Calendar */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 pb-4">
              <div className="xl:col-span-2">
                <TopMoversTable filter={selectedFilter} />
              </div>
              <div className="xl:col-span-1">
                <EconomicCalendar />
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Alert Creation Slide-over Panel */}
      {showAlertPanel && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={() => setShowAlertPanel(false)} />
          <div className="w-full max-w-lg bg-[#1E293B] border-l border-[#334155] overflow-y-auto flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#334155] shrink-0">
              <div className="flex items-center gap-2">
                <Icon name="BellIcon" size={16} variant="solid" className="text-sky-500" />
                <h2 className="text-sm font-semibold text-slate-200 font-mono">Create Market Alert</h2>
              </div>
              <div className="flex items-center gap-3">
                {alertCount > 0 && (
                  <span className="text-xs font-mono text-slate-500">{alertCount} active alert{alertCount !== 1 ? 's' : ''}</span>
                )}
                <button onClick={() => setShowAlertPanel(false)} className="text-slate-500 hover:text-slate-300">
                  <Icon name="XMarkIcon" size={20} variant="outline" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-5">
              <AlertCreationPanel
                onClose={() => setShowAlertPanel(false)}
                onCreated={() => { fetchAlertCount(); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}